<template>
  <BaseModal
    :heading="
      modalData && modalData.id
        ? $t('global.actions.edit_customer')
        : $t('global.actions.new_customer')
    "
    small
    useForm
    testId="create-customer"
    @submit="submit"
  >
    <Form
      v-model:firstName="formData.firstName"
      v-model:lastName="formData.lastName"
      v-model:email="formData.email"
      v-model:phone="formData.mobilePhone"
      v-model:address="addressFormData.address"
      v-model:zipcode="addressFormData.zipcode"
      v-model:city="addressFormData.city"
      v-model:dateOfBirth="medicalFormData.dateOfBirth"
      v-model:personalRegistrationNumber="medicalFormData.personalRegistrationNumber"
      v-model:insuranceProvider="medicalFormData.insuranceProvider"
      :requireAddress="requireAddress"
      :requireMedicalData="requireMedicalData"
    />
    <template #footer>
      <BaseButton
        :loading="isLoading"
        submitForm
        v-test="'customer-submit'"
      >
        {{ $t('global.actions.save') }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { computed, onMounted, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import { customerFragment } from '@/graphql-fragments';
import { flash } from '@/helpers/ui';
import type { Customer } from '@/types';
import Form from './Form.vue';

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  mobilePhone: string;
}

interface AddressFormData {
  address: string;
  zipcode: string;
  city: string;
}

interface MedicalFormData {
  dateOfBirth?: string;
  personalRegistrationNumber: string;
  insuranceProvider: string;
}

const { t } = useI18n();

const props = withDefaults(
  defineProps<{
    modalData: any;
    requireAddress?: boolean;
    requireMedicalData?: boolean;
  }>(),
  {
    requireAddress: false,
    requireMedicalData: false,
  },
);

const emit = defineEmits<{
  submit: [customer: any];
}>();

const formData = reactive<FormData>({
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: '',
});

const addressFormData = reactive<AddressFormData>({
  address: '',
  zipcode: '',
  city: '',
});

const medicalFormData = reactive<MedicalFormData>({
  personalRegistrationNumber: '',
  insuranceProvider: '',
});

const { mutate: createCustomer, loading: createCustomerLoading } =
  useMutation(gql`
  mutation createCustomer($input: CreateCustomerInput!) {
    createCustomer(input: $input) {
      customer {
        ...customer
        address
        zipcode
        city
        dateOfBirth
        personalRegistrationNumber
        insuranceProvider
      }
      errors
    }
  }
  ${customerFragment}
`);

const { mutate: updateCustomer, loading: updateCustomerLoading } =
  useMutation(gql`
  mutation updateCustomer($input: UpdateCustomerInput!) {
    updateCustomer(input: $input) {
      customer {
        ...customer
        address
        zipcode
        city
        dateOfBirth
        personalRegistrationNumber
        insuranceProvider
      }
      errors
    }
  }
  ${customerFragment}
`);

const isLoading = computed(
  () => createCustomerLoading.value || updateCustomerLoading.value,
);

const submit = () => {
  const customerAttributes = {
    ...formData,
    ...(props.requireAddress ? { ...addressFormData } : {}),
    ...(props.requireMedicalData
      ? { ...medicalFormData, dateOfBirth: medicalFormData.dateOfBirth || null }
      : {}),
  };

  const payload = props.modalData.id
    ? {
        id: props.modalData.id,
        customerAttributes,
      }
    : {
        customerAttributes,
      };

  if (props.modalData.id) {
    updateCustomer({ input: payload }).then(({ data }) => {
      afterMutation(data.updateCustomer.customer);
    });
  } else {
    createCustomer({ input: payload }).then(({ data }) => {
      afterMutation(data.createCustomer.customer);
    });
  }
};

const afterMutation = (customer: Customer) => {
  emit('submit', customer);
  flash(
    t(
      `global.flash.${props.modalData.id ? 'customer_updated' : 'customer_created'}`,
    ),
  );
};

onMounted(() => {
  if (props.modalData) {
    formData.firstName = props.modalData.firstName;
    formData.lastName = props.modalData.lastName;
    formData.email = props.modalData.email;
    formData.mobilePhone = props.modalData.mobilePhone || props.modalData.phone;

    if (props.requireAddress) {
      addressFormData.address = props.modalData.address;
      addressFormData.zipcode = props.modalData.zipcode;
      addressFormData.city = props.modalData.city;
    }
    if (props.requireMedicalData) {
      medicalFormData.dateOfBirth = props.modalData.dateOfBirth;
      medicalFormData.personalRegistrationNumber =
        props.modalData.personalRegistrationNumber;
      medicalFormData.insuranceProvider = props.modalData.insuranceProvider;
    }
  }
});
</script>
